// Mock fixtures for the Movement Assignment (Plan) prototype. FE-only — no API,
// no backend. Numbers/names mirror the design mock so the layout reads true;
// everything here is replaced by real Loads + full-load Orders + Resources when
// the screen is wired up.

export type MovementStatus =
  | 'Standing'
  | 'Planned'
  | 'En Route'
  | 'On Ferry'
  | 'Delivered';

export type Probability = 'On time' | 'At risk' | 'Tight';
export type WindowKind = 'fixed' | 'flex';

export interface MovementRow {
  id: string;
  jobNo: string;
  /** 'load' = built multi-drop load; 'fullLoad' = full-load order (becomes a Load on assign). */
  kind: 'load' | 'fullLoad';
  customer: string;
  keyAccount: boolean;
  cargo: string[]; // badge labels e.g. ['Full Load'] or ['GDP Refrig']
  origin: string;
  dest: string;
  ferry: boolean;
  distKm: number;
  durLabel: string;
  window: { label: string; kind: WindowKind };
  prob: Probability;
  driver: string | null;
  vehicle: string | null;
  status: MovementStatus;
  co2Kg: number;
  costEur: number;
  emptyKm: number;
}

export const MOVEMENTS: MovementRow[] = [
  {
    id: 'm1', jobNo: '2604-0114', kind: 'fullLoad', customer: 'Kingspan Group', keyAccount: true,
    cargo: ['Full Load'], origin: 'IE', dest: 'GB', ferry: true, distKm: 520, durLabel: '8.5h',
    window: { label: '06:00', kind: 'fixed' }, prob: 'On time', driver: 'Peter Szobo', vehicle: '241C7318',
    status: 'On Ferry', co2Kg: 148, costEur: 420, emptyKm: 0,
  },
  {
    id: 'm2', jobNo: '318630', kind: 'load', customer: 'Pfizer Ireland P.', keyAccount: true,
    cargo: ['GDP Refrig'], origin: 'IE', dest: 'IE', ferry: false, distKm: 42, durLabel: '1.2h',
    window: { label: '07:00', kind: 'fixed' }, prob: 'At risk', driver: null, vehicle: null,
    status: 'Standing', co2Kg: 12, costEur: 95, emptyKm: 0,
  },
  {
    id: 'm3', jobNo: '2604-0110', kind: 'fullLoad', customer: 'Ornua', keyAccount: false,
    cargo: ['GDP Refrig'], origin: 'IE', dest: 'GB', ferry: true, distKm: 2100, durLabel: '3d',
    window: { label: '14:30–17:00', kind: 'flex' }, prob: 'Tight', driver: 'A. Healy', vehicle: '211C6865',
    status: 'En Route', co2Kg: 598, costEur: 1840, emptyKm: 0,
  },
  {
    id: 'm4', jobNo: '306327', kind: 'load', customer: 'BAYER GmbH', keyAccount: false,
    cargo: ['GDP Refrig'], origin: 'IE', dest: 'IE', ferry: false, distKm: 112, durLabel: '2.2h',
    window: { label: '06:30', kind: 'fixed' }, prob: 'At risk', driver: null, vehicle: null,
    status: 'Standing', co2Kg: 32, costEur: 210, emptyKm: 0,
  },
  {
    id: 'm5', jobNo: '310017', kind: 'load', customer: 'Pfizer Ireland P.', keyAccount: true,
    cargo: ['GDP Refrig', 'ADR'], origin: 'IE', dest: 'IE', ferry: false, distKm: 88, durLabel: '1.8h',
    window: { label: '08:30', kind: 'flex' }, prob: 'On time', driver: 'Dan Crowley', vehicle: '191C6234',
    status: 'Planned', co2Kg: 26, costEur: 180, emptyKm: 4,
  },
  {
    id: 'm6', jobNo: '309031', kind: 'load', customer: 'BAYER GmbH', keyAccount: false,
    cargo: ['GDP Refrig'], origin: 'IE', dest: 'IE', ferry: false, distKm: 64, durLabel: '1.4h',
    window: { label: '14:00', kind: 'flex' }, prob: 'On time', driver: 'Dan Crowley', vehicle: '191C6234',
    status: 'Planned', co2Kg: 19, costEur: 140, emptyKm: 6,
  },
  {
    id: 'm7', jobNo: '305824', kind: 'load', customer: 'Coen Steel', keyAccount: false,
    cargo: ['Full Load'], origin: 'IE', dest: 'IE', ferry: false, distKm: 70, durLabel: '1.5h',
    window: { label: '06:00', kind: 'flex' }, prob: 'On time', driver: 'Shawn Lewis', vehicle: '191C123',
    status: 'Delivered', co2Kg: 21, costEur: 150, emptyKm: 0,
  },
];

/** Queue filter buckets. Counts are derived from MOVEMENTS by the component. */
export const QUEUE_FILTERS = ['All', 'Standing', 'Unassigned', 'At Risk', 'In Progress', 'Planned'] as const;
export type QueueFilter = (typeof QUEUE_FILTERS)[number];

export function matchesFilter(m: MovementRow, f: QueueFilter): boolean {
  switch (f) {
    case 'All':
      return true;
    case 'Standing':
      return m.status === 'Standing';
    case 'Unassigned':
      return m.driver === null;
    case 'At Risk':
      return m.prob === 'At risk';
    case 'In Progress':
      return m.status === 'En Route' || m.status === 'On Ferry';
    case 'Planned':
      return m.status === 'Planned';
  }
}

// ── Gantt ────────────────────────────────────────────────────────────────────

export const GANTT_START_HOUR = 5;
export const GANTT_END_HOUR = 21;
export const GANTT_NOW_HOUR = 9.25; // 09:15

export interface GanttBar {
  jobNo: string;
  customer: string;
  badges: string[];
  startHour: number;
  endHour: number;
  status: MovementStatus;
}

export interface GanttRow {
  driver: string;
  vehicle: string;
  bars: GanttBar[];
}

export const GANTT_ROWS: GanttRow[] = [
  {
    driver: 'Peter Szobo', vehicle: '241C7318',
    bars: [{ jobNo: '2604-0114', customer: 'Kingspan', badges: ['GDP'], startHour: 6.25, endHour: 17, status: 'On Ferry' }],
  },
  {
    driver: 'Dan Crowley', vehicle: '191C6234',
    bars: [
      { jobNo: '310017', customer: 'Pfizer', badges: ['GDP', 'ADR'], startHour: 7.5, endHour: 10, status: 'Planned' },
      { jobNo: '309031', customer: 'BAYER', badges: ['GDP'], startHour: 14, endHour: 17, status: 'Planned' },
    ],
  },
  {
    driver: 'Antoinette Healy', vehicle: '211C6865',
    bars: [{ jobNo: '2604-0110', customer: 'Ornua', badges: ['GDP', 'TEMP'], startHour: 5.5, endHour: 17, status: 'En Route' }],
  },
  {
    driver: 'Marcin Zabor', vehicle: '251C105',
    bars: [{ jobNo: '324963', customer: 'Binochem', badges: [], startHour: 7.5, endHour: 12.5, status: 'Delivered' }],
  },
  { driver: 'James Doody', vehicle: '222C4712', bars: [] },
  {
    driver: 'Shawn Lewis', vehicle: '191C123',
    bars: [{ jobNo: '305824', customer: 'Coen', badges: [], startHour: 5.5, endHour: 10, status: 'Delivered' }],
  },
];

// ── Driver roster ─────────────────────────────────────────────────────────--

export interface RosterDriver {
  id: string;
  name: string;
  fit: boolean;
  available: boolean;
  startLabel: string; // "07:00 · Dublin"
  vehicleReg: string;
  tachoHours: number | null;
  tachoMax: number;
  capabilities: string[];
  recentCustomers: string[];
  onTimePct: number | null;
}

export const ROSTER: RosterDriver[] = [
  { id: 'd1', name: 'Dan Crowley', fit: true, available: true, startLabel: '07:00 · Dublin', vehicleReg: '191C6234', tachoHours: 2.5, tachoMax: 9, capabilities: ['GDP', 'ADR'], recentCustomers: ['Pfizer Ring', 'SCL Pharma'], onTimePct: 89 },
  { id: 'd2', name: 'Khangelani E.', fit: true, available: true, startLabel: '06:00 · Dublin', vehicleReg: '211C6873', tachoHours: 7, tachoMax: 9, capabilities: ['GDP', 'HAZ', 'ADR'], recentCustomers: ['Pfizer Ring', 'SCL Pharma'], onTimePct: 94 },
  { id: 'd3', name: 'Peter Szobo', fit: false, available: true, startLabel: '06:00 · Dublin', vehicleReg: '241C7318', tachoHours: 5, tachoMax: 9, capabilities: ['GDP', 'TEMP'], recentCustomers: ['Pfizer Ring', 'SCL Pharma'], onTimePct: 91 },
  { id: 'd4', name: 'A. Healy', fit: false, available: true, startLabel: '08:00 · Cork', vehicleReg: '211C6865', tachoHours: null, tachoMax: 9, capabilities: ['ADR'], recentCustomers: ['National C.', 'Coen'], onTimePct: 87 },
  { id: 'd5', name: 'James Doody', fit: false, available: true, startLabel: '05:00 · Dublin', vehicleReg: '222C4712', tachoHours: 8.5, tachoMax: 9, capabilities: ['GDP', 'FOOD'], recentCustomers: ['Binochem', 'Hovione'], onTimePct: 92 },
  { id: 'd6', name: 'Marcin Zabor', fit: false, available: true, startLabel: '07:00 · Dublin', vehicleReg: '251C105', tachoHours: 5, tachoMax: 9, capabilities: ['GDP'], recentCustomers: ['National C.', 'SCL Pharma'], onTimePct: 88 },
  { id: 'd7', name: 'Shawn Lewis', fit: false, available: true, startLabel: '07:00 · Dublin', vehicleReg: '191C123', tachoHours: 5, tachoMax: 9, capabilities: ['GDP'], recentCustomers: ['National C.', 'Hovione'], onTimePct: 88 },
  { id: 'd8', name: 'Rafal Nowak', fit: false, available: false, startLabel: 'Off shift · Dublin', vehicleReg: '222C4615', tachoHours: null, tachoMax: 9, capabilities: ['GDP'], recentCustomers: ['Pfizer Ring'], onTimePct: 78 },
];

// ── "my i" Movement Intelligence ────────────────────────────────────────────

export interface FitDriver {
  name: string;
  availLabel: string;
  statLabel: string;
}

export interface SlaAlert {
  jobNo: string;
  title: string;
  body: string;
  action: string;
  severity: 'high' | 'medium';
}

export interface MovementIntel {
  movementId: string;
  fitDrivers: FitDriver[];
  feasibility: string | null;
  driverBrief: string;
  slaAlerts: SlaAlert[];
}

/** Keyed by movement id; the rail shows the selected movement's intel. */
export const INTEL: Record<string, MovementIntel> = {
  m2: {
    movementId: 'm2',
    fitDrivers: [
      { name: 'Dan Crowley', availLabel: '6.5h avail', statLabel: '89% on-time GDP' },
      { name: 'Khangelani E.', availLabel: '2h avail', statLabel: '94% on-time GDP' },
    ],
    feasibility: 'Fixed window has passed. Assign and notify customer immediately.',
    driverBrief:
      'Hi — urgent. Pfizer Ireland, Ringaskiddy, Cork. Fixed 07:00 window has passed. GDP refrigerated +2/+8°C, 4 pallets. SLA breach — assign and depart immediately. Notify site supervisor on arrival. Job ref: 318630.',
    slaAlerts: [
      { jobNo: '318630', title: 'Pfizer — Fixed 07:00 missed', body: 'Current time 09:14 · +2h 14min overdue · Unassigned. Customer notification required · GDP Refrig · Key account', action: 'Assign driver', severity: 'high' },
      { jobNo: '306327', title: 'BAYER — Fixed 06:30 unassigned', body: 'SLA penalty clause active · GDP Refrig · Kildare. Dan Crowley: GDP+ADR · 6.5h remaining · Feasible now', action: 'Assign Dan Crowley', severity: 'medium' },
    ],
  },
};

export const DEFAULT_INTEL: MovementIntel = INTEL.m2!;
